/* ============================================================================
   HighGrade storefront behaviour.

   Deliberately plain: one classic script, no build step, no framework. Horizon's own
   web components stay where they are used; nothing here depends on them.
   ============================================================================ */
(function () {
  'use strict';

  var HG = window.HighGrade || {};
  window.HighGrade = HG;

  /* ---------- money ----------
     Formatted from the shop's own money_format rather than Intl with the request locale.
     This store's primary locale is still 'en', so Intl rendered the drawer as EUR124.99
     next to server-rendered prices of 124,99 EUR on the same page. The shop setting is the
     single source of truth for how a price looks. */
  function group(num, thousands, decimal) {
    var parts = (Math.abs(num) / 100).toFixed(2).split('.');
    var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return (num < 0 ? '-' : '') + whole + (decimal ? decimal + parts[1] : '');
  }

  function money(cents) {
    cents = Math.round(cents || 0);
    var format = HG.moneyFormat || '{{amount}}';
    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, name) {
      switch (name) {
        case 'amount': return group(cents, ',', '.');
        case 'amount_no_decimals': return group(cents - (cents % 100), ',', '');
        case 'amount_with_comma_separator': return group(cents, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return group(cents - (cents % 100), '.', '');
        case 'amount_with_apostrophe_separator': return group(cents, "'", '.');
        case 'amount_with_space_separator': return group(cents, ' ', ',');
        case 'amount_no_decimals_with_space_separator': return group(cents - (cents % 100), ' ', '');
        default: return group(cents, ',', '.');
      }
    });
  }
  HG.money = money;

  /* A variant named "25" reads as a bare number in the cart, where the PDP chip says 25 g.
     The real fix is naming the variants properly in admin; until then the unit is appended
     so one page does not contradict another. HG.weightUnit comes from theme settings. */
  function variantLabel(title) {
    if (!title || title === 'Default Title') return '';
    if (HG.weightUnit && /^[0-9]+([.,][0-9]+)?$/.test(title.trim())) {
      return title.trim() + ' ' + HG.weightUnit;
    }
    return title;
  }

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  // boot() runs again on shopify:section:load in the theme editor. Anything that attaches a
  // listener claims its element first, so a re-boot rebinds only genuinely new markup - a
  // second delegated handler on the cart would make one click change the quantity twice.
  function claim(el, key) {
    if (!el) return false;
    var flag = 'hg' + key;
    if (el.dataset[flag]) return false;
    el.dataset[flag] = '1';
    return true;
  }

  /* ---------- age gate ----------
     18+ is a legal requirement, not a marketing interstitial. The answer is remembered so
     it is not asked on every page of a normal session. */
  var GATE_KEY = 'hg-age-ok';
  function initAgeGate() {
    var gate = $('#hg-agegate');
    if (!gate) return;
    var stored;
    try { stored = localStorage.getItem(GATE_KEY); } catch (e) { stored = null; }
    if (stored === 'yes') { gate.remove(); return; }
    gate.hidden = false;
    document.body.style.overflow = 'hidden';
    var yes = $('[data-age-yes]', gate);
    if (yes) {
      yes.addEventListener('click', function () {
        try { localStorage.setItem(GATE_KEY, 'yes'); } catch (e) { /* private mode */ }
        gate.remove();
        document.body.style.overflow = '';
      });
    }
  }

  /* ---------- cookie banner ----------
     "Nur notwendige" is a genuine equal-weight option, not a greyed-out decoy. */
  var COOKIE_KEY = 'hg-cookie-choice';
  function initCookie() {
    var bar = $('#hg-cookie');
    if (!bar) return;
    var stored;
    try { stored = localStorage.getItem(COOKIE_KEY); } catch (e) { stored = null; }
    if (stored) { bar.remove(); return; }
    bar.hidden = false;
    $$('[data-cookie-choice]', bar).forEach(function (btn) {
      btn.addEventListener('click', function () {
        try { localStorage.setItem(COOKIE_KEY, btn.getAttribute('data-cookie-choice')); } catch (e) {}
        bar.remove();
      });
    });
  }

  /* ---------- mobile nav ---------- */
  function initNav() {
    var shell = $('#hg-navshell');
    if (!claim(shell, 'Nav')) return;
    function open() { shell.setAttribute('data-open', ''); document.body.style.overflow = 'hidden'; }
    function close() { shell.removeAttribute('data-open'); document.body.style.overflow = ''; }
    $$('[data-nav-open]').forEach(function (b) { b.addEventListener('click', open); });
    $$('[data-nav-close]', shell).forEach(function (b) { b.addEventListener('click', close); });
    HG.closeNav = close;
  }

  /* ---------- inline search ---------- */
  function initSearch() {
    var wrap = $('#hg-searchwrap');
    if (!claim(wrap, 'Search')) return;
    var input = $('input[type="search"]', wrap);
    $$('[data-search-open]', wrap).forEach(function (b) {
      b.addEventListener('click', function () {
        wrap.setAttribute('data-open', '');
        setTimeout(function () { if (input) input.focus(); }, 120);
      });
    });
    $$('[data-search-close]', wrap).forEach(function (b) {
      b.addEventListener('click', function () { wrap.removeAttribute('data-open'); });
    });
    HG.closeSearch = function () { wrap.removeAttribute('data-open'); };
  }

  /* ---------- cart drawer ----------
     Talks to the Cart AJAX API directly. Every mutation re-reads /cart.js rather than
     trusting a local copy, so the badge, the lines and the total can never disagree. */
  var cartState = null;

  function cartFetch(url, body) {
    return fetch(url, {
      method: body ? 'POST' : 'GET',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined
    }).then(function (r) { return r.json(); });
  }

  /* Anything that reaches innerHTML goes through this. Line item properties are the one
     place in the cart where a value can originate from a URL or a crafted /cart/add.js
     call rather than from the catalogue, so they are never trusted. */
  function esc(v) {
    return String(v == null ? '' : v)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function renderCart(cart) {
    cartState = cart;
    var count = cart.item_count || 0;

    $$('[data-cart-count]').forEach(function (el) {
      el.textContent = count;
      if (count) el.removeAttribute('data-zero'); else el.setAttribute('data-zero', '');
    });

    var box = $('#hg-cartitems');
    if (box) {
      if (!cart.items.length) {
        box.innerHTML = '<p class="hg-note" style="padding:26px 24px">Dein Warenkorb ist leer.</p>';
      } else {
        box.innerHTML = cart.items.map(function (item, i) {
          // No <img src=""> placeholder. An empty src renders as a broken-image glyph in
          // every browser, which is what a product with no media looked like here.
          var img = item.image
            ? '<img src="' + item.image.replace(/(\.[a-z]+)(\?|$)/i, '_128x128$1$2') + '" alt="">'
            : '<span class="li-noimg" aria-hidden="true"></span>';

          // The per-unit price is deliberately not rendered. Removed from the storefront on
          // merchant instruction 25.08.2026; the Liquid side went with hg-grundpreis and this
          // was the drawer's own copy of it, reading Shopify's native unit_price.

          var vt = variantLabel(item.variant_title);
          var opts = vt ? '<div class="hg-small">' + vt + '</div>' : '';

          // Line item properties. This is how a Mix-Box shows what is actually in it: the
          // builder posts { 'Deine Mischung': 'Gelato 10 g, Runtz 15 g' }, and without this
          // the drawer showed a box with no indication of its contents. Keys starting with
          // an underscore are Shopify's convention for hidden properties and stay hidden.
          // Values are escaped: they are echoed back into innerHTML.
          var props = '';
          if (item.properties) {
            Object.keys(item.properties).forEach(function (k) {
              var v = item.properties[k];
              if (!v || k.charAt(0) === '_') return;
              props += '<div class="li-prop"><span class="k">' + esc(k) + '</span>'
                + String(v).split(',').map(function (part) {
                    return '<span class="v">' + esc(part.trim()) + '</span>';
                  }).join('')
                + '</div>';
            });
          }
          return '<div class="li">' + img +
            '<div style="flex:1;min-width:0">' +
              '<b>' + esc(item.product_title) + '</b>' + opts + props +
              '<div class="li-ctl">' +
                '<div class="hg-qty" style="width:fit-content;border-width:2px;box-shadow:0 2px 0 var(--ink)">' +
                  '<button type="button" style="width:36px;height:36px" data-cart-line="' + (i + 1) + '" data-cart-delta="-1" aria-label="Weniger">&minus;</button>' +
                  '<span style="min-width:34px;text-align:center;font-weight:800">' + item.quantity + '</span>' +
                  '<button type="button" style="width:36px;height:36px" data-cart-line="' + (i + 1) + '" data-cart-delta="1" aria-label="Mehr">+</button>' +
                '</div>' +
                '<button type="button" class="hg-del" data-cart-line="' + (i + 1) + '" data-cart-remove="1" ' +
                  'aria-label="' + esc(item.product_title) + ' aus dem Warenkorb entfernen">' +
                  '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><path d="M5 5l14 14M19 5L5 19"/></svg>' +
                  '<span>Entfernen</span>' +
                '</button>' +
              '</div>' +
            '</div>' +
            '<b>' + money(item.final_line_price) + '</b>' +
          '</div>';
        }).join('');
      }
    }

    var sum = $('#hg-cartsum');
    if (sum) sum.textContent = money(cart.total_price);
    var checkout = $('#hg-checkout');
    if (checkout) checkout.disabled = !cart.items.length;
  }

  function refreshCart() { return cartFetch('/cart.js').then(renderCart); }

  function openCart() {
    var shell = $('#hg-cartshell');
    if (shell) shell.setAttribute('data-open', '');
  }
  function closeCart() {
    var shell = $('#hg-cartshell');
    if (shell) shell.removeAttribute('data-open');
  }
  HG.openCart = openCart;
  HG.refreshCart = refreshCart;

  // The buttons that open the drawer live in the header, which the editor can reload on its
  // own, so they are bound separately from the drawer itself.
  function initCartTriggers() {
    $$('[data-cart-open]').forEach(function (b) {
      if (!claim(b, 'CartOpen')) return;
      b.addEventListener('click', function (e) { e.preventDefault(); openCart(); });
    });
  }

  function initCart() {
    var shell = $('#hg-cartshell');
    if (!claim(shell, 'Cart')) return;

    $$('[data-cart-close]', shell).forEach(function (b) { b.addEventListener('click', closeCart); });

    shell.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-cart-line]');
      if (!btn) return;
      var line = parseInt(btn.getAttribute('data-cart-line'), 10);
      var item = cartState && cartState.items[line - 1];
      if (!item) return;

      // Remove is quantity 0, not a decrement. Pressing minus on a line of three should
      // step it to two; deleting is its own button and takes the whole line.
      var qty;
      if (btn.hasAttribute('data-cart-remove')) {
        qty = 0;
      } else {
        var delta = parseInt(btn.getAttribute('data-cart-delta'), 10);
        if (isNaN(delta)) return;
        qty = Math.max(0, item.quantity + delta);
      }
      cartFetch('/cart/change.js', { line: line, quantity: qty }).then(renderCart);
    });

    refreshCart();
  }

  /* Add-to-cart on any form marked data-hg-cart-form: post it and open the drawer instead
     of navigating to /cart. Falls back to a normal submit if the request fails. */
  function initAddForms() {
    $$('form[data-hg-cart-form]').forEach(function (form) {
      if (!claim(form, 'AddForm')) return;
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var btn = form.querySelector('[type="submit"]');
        if (btn) btn.disabled = true;
        fetch('/cart/add.js', { method: 'POST', body: new FormData(form) })
          .then(function (r) {
            if (!r.ok) throw new Error('add failed');
            return r.json();
          })
          .then(function () { return refreshCart(); })
          .then(function () { openCart(); })
          .catch(function () { form.submit(); })
          .finally(function () { if (btn) btn.disabled = false; });
      });
    });
  }

  /* ---------- quantity steppers ---------- */
  function initQty() {
    $$('[data-qty]').forEach(function (wrap) {
      if (!claim(wrap, 'Qty')) return;
      var input = $('input', wrap);
      $$('[data-qty-delta]', wrap).forEach(function (b) {
        b.addEventListener('click', function () {
          var d = parseInt(b.getAttribute('data-qty-delta'), 10);
          var v = Math.max(1, (parseInt(input.value, 10) || 1) + d);
          input.value = v;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    });
  }

  /* ---------- product gallery ---------- */
  function initGallery() {
    var gal = $('#hg-gallery');
    if (!claim(gal, 'Gallery')) return;
    var main = $('#hg-gal-main', gal);
    var cap = $('#hg-gal-cap', gal);
    var no = $('#hg-gal-no', gal);
    var thumbs = $$('.hg-thumb', gal);

    thumbs.forEach(function (t, i) {
      t.addEventListener('click', function () {
        thumbs.forEach(function (x) { x.removeAttribute('data-on'); });
        t.setAttribute('data-on', '');
        if (main) {
          main.src = t.getAttribute('data-full');
          main.srcset = t.getAttribute('data-fullset') || '';
          main.alt = t.getAttribute('data-alt') || '';
        }
        if (cap) cap.textContent = t.getAttribute('data-caption') || '';
        if (no) no.textContent = (i + 1) + ' / ' + thumbs.length;
      });
    });
  }

  /* ---------- variant chips that double as the price ladder ---------- */
  function initVariants() {
    var picker = $('#hg-variants');
    if (!claim(picker, 'Variants')) return;
    var chips = $$('.hg-sw', picker);
    var idInput = $('#hg-variant-id');
    var priceEl = $('#hg-price');
    var wasEl = $('#hg-was');
    var gpEl = $('#hg-gp');
    var addBtn = $('#hg-add');

    chips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        if (chip.hasAttribute('data-soldout')) return;
        chips.forEach(function (c) { c.removeAttribute('data-on'); });
        chip.setAttribute('data-on', '');

        if (idInput) idInput.value = chip.getAttribute('data-variant-id');
        if (priceEl) priceEl.innerHTML = chip.getAttribute('data-price');
        if (wasEl) {
          var was = chip.getAttribute('data-compare');
          wasEl.innerHTML = was || '';
          wasEl.hidden = !was;
        }
        if (gpEl) gpEl.innerHTML = chip.getAttribute('data-gp') || '';
        if (addBtn) addBtn.disabled = false;

        // Keep the URL shareable without reloading the page.
        if (window.history && history.replaceState) {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', chip.getAttribute('data-variant-id'));
          history.replaceState({}, '', url);
        }
      });
    });
  }

  /* ---------- price ladder: a content section that acts on the buy box ----------
     Every rung is a real <a href="?variant=..."> rendered by the server, so the ladder is
     crawlable, indexed and works with JavaScript off. What this adds is the shortcut:
     picking a size down here selects it up there and scrolls the buy box into view,
     instead of costing a page load.

     preventDefault only ever fires when the matching chip is actually on the page. If the
     buy box is missing, or the variant is sold out, the link is left to do its own job. */
  function setLadderState(ladder, id) {
    $$('.hg-rung', ladder).forEach(function (rung) {
      var on = rung.getAttribute('data-variant-id') === id;
      if (on) {
        rung.setAttribute('data-on', '');
        rung.setAttribute('aria-current', 'true');
      } else {
        rung.removeAttribute('data-on');
        rung.removeAttribute('aria-current');
      }
    });
  }

  function initPriceLadder() {
    var ladder = $('[data-hg-ladder]');
    if (!ladder) return;
    var picker = $('#hg-variants');

    if (claim(ladder, 'Ladder')) {
      $$('.hg-rung', ladder).forEach(function (rung) {
        rung.addEventListener('click', function (e) {
          var id = rung.getAttribute('data-variant-id');
          var chip = picker && picker.querySelector('.hg-sw[data-variant-id="' + id + '"]');
          if (!chip || chip.hasAttribute('data-soldout')) return;
          e.preventDefault();
          chip.click();
          setLadderState(ladder, id);
          var target = $('.hg-buy') || picker;
          if (target) {
            var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' });
          }
        });
      });
    }

    // The other direction: a chip pressed in the buy box moves the ladder's marker too,
    // so the two never disagree about which size is selected. Claimed separately from
    // initVariants, which owns the picker's own behaviour.
    /* The ladder is resolved at event time rather than captured in the closure. The flag
       lives on the picker but the element it drives belongs to a different section, so a
       shopify:section:load on the ladder alone re-runs boot(), rebinds the new ladder and
       leaves this handler still pointing at the detached old one - after which pressing a
       size chip stopped moving the marker, in the theme editor only. */
    if (picker && claim(picker, 'LadderSync')) {
      picker.addEventListener('click', function (e) {
        var chip = e.target.closest('.hg-sw');
        if (!chip) return;
        var live = document.querySelector('[data-hg-ladder]');
        if (live) setLadderState(live, chip.getAttribute('data-variant-id'));
      });
    }
  }

  /* ---------- flavour band: selecting a flavour repaints the whole band ---------- */
  function initFlavourBand() {
    $$('[data-flavour-band]').forEach(function (band) {
      if (!claim(band, 'Band')) return;
      var skies = $$('[data-sky]', band);
      var picks = $$('.hg-fpick', band);
      var packs = $$('[data-pack]', band);
      var copies = $$('[data-copy]', band);

      /* Three flavours can each carry a background video, and only one is ever on
         screen. preload="none" keeps the other two off the wire until picked; this
         keeps them off the CPU afterwards. Honour reduced motion here too: CSS already
         hides them, and starting playback on a hidden element is pure waste. */
      var stillPlease = window.matchMedia
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      function select(key) {
        skies.forEach(function (s) {
          var on = s.getAttribute('data-sky') === key;
          if (on) s.setAttribute('data-on', '');
          else s.removeAttribute('data-on');
          if (s.tagName !== 'VIDEO') return;
          if (on && !stillPlease) {
            /* Autoplay can be refused; a rejected promise here is not an error. */
            var playing = s.play();
            if (playing && playing.catch) playing.catch(function () {});
          } else {
            s.pause();
          }
        });
        packs.forEach(function (p) { p.hidden = p.getAttribute('data-pack') !== key; });
        copies.forEach(function (c) { c.hidden = c.getAttribute('data-copy') !== key; });
        picks.forEach(function (p) {
          if (p.getAttribute('data-pick') === key) p.setAttribute('data-on', '');
          else p.removeAttribute('data-on');
        });
      }

      picks.forEach(function (p) {
        p.addEventListener('click', function () { select(p.getAttribute('data-pick')); });
      });
      if (picks.length) select(picks[0].getAttribute('data-pick'));
    });
  }

  /* ---------- mix builder ----------
     The per-gram rate comes from the TOTAL box size, not the individual strain. A mixed
     25 g box costs the 25 g rate; anything else would punish the customer for mixing,
     which is the entire point of the feature. */
  function initMix() {
    var root = $('#hg-mix');
    if (!claim(root, 'Mix')) return;

    var dataEl = $('#hg-mix-data');
    var data;
    try { data = JSON.parse(dataEl.textContent); } catch (e) { return; }

    var sizes = data.sizes || [];
    var strains = data.strains || [];
    var size = sizes.length ? sizes[0].grams : 25;
    var picked = {};

    var listEl = $('#hg-mix-list', root);
    var sizeEl = $('#hg-mix-sizes', root);
    var meter = $('#hg-mix-meter', root);
    var hint = $('#hg-mix-hint', root);
    var lines = $('#hg-mix-lines', root);
    var totalEl = $('#hg-mix-total', root);
    var gpEl = $('#hg-mix-gp', root);
    var addBtn = $('#hg-mix-add', root);

    function used() {
      return Object.keys(picked).reduce(function (s, k) { return s + picked[k]; }, 0);
    }
    function sizeEntry(g) {
      for (var i = 0; i < sizes.length; i++) if (sizes[i].grams === g) return sizes[i];
      return sizes[0];
    }
    function unitPrice() {
      var e = sizeEntry(size);
      return e ? e.price / e.grams : 0;
    }

    function render() {
      var u = used();

      sizeEl.innerHTML = sizes.map(function (s) {
        return '<button type="button" class="hg-sizebtn"' + (s.grams === size ? ' data-on' : '') +
          ' data-size="' + s.grams + '">' + s.grams + ' g</button>';
      }).join('');

      listEl.innerHTML = strains.map(function (p) {
        var g = picked[p.handle] || 0;
        return '<div class="hg-mixrow">' +
          '<span class="th">' + (p.image ? '<img src="' + p.image + '" alt="">' : '') + '</span>' +
          '<span class="info"><span class="nm">' + p.title + '</span>' +
            /* No CBD percentage here. docs/seo/17-store-facts.md: no real lab data
               exists outside Carolina Kush, so seven of eight strains would print an
               invented figure as a product specification. The product page removed the
               same figures; the box builder kept them. */
            '<span class="sub">' + p.kind + '</span></span>' +
          '<span class="hg-stepper">' +
            '<button type="button" data-mix="' + p.handle + '" data-delta="-' + data.step + '"' +
              (g < data.step ? ' disabled' : '') + ' aria-label="Weniger">&minus;</button>' +
            '<span>' + g + ' g</span>' +
            '<button type="button" data-mix="' + p.handle + '" data-delta="' + data.step + '"' +
              (u + data.step > size ? ' disabled' : '') + ' aria-label="Mehr">+</button>' +
          '</span></div>';
      }).join('');

      meter.style.width = Math.round(u / size * 100) + '%';
      hint.textContent = u === size
        ? 'Box voll. Fertig zum Hinzufügen.'
        : 'Noch ' + (size - u) + ' g frei von ' + size + ' g';

      var keys = Object.keys(picked).filter(function (k) { return picked[k] > 0; });
      lines.innerHTML = keys.length
        ? keys.map(function (k) {
            var p = strains.filter(function (x) { return x.handle === k; })[0];
            return '<div class="hg-mixline"><span>' + p.title + '</span>' +
              '<span class="g">' + picked[k] + ' g</span></div>';
          }).join('')
        : '<p class="hg-small">Noch nichts gewählt.</p>';

      var unit = unitPrice();
      totalEl.textContent = money(Math.round(unit * u));
      // Per-kilo removed from the storefront on merchant instruction, 25.08.2026. The
      // Liquid side went with snippets/hg-grundpreis.liquid; this line is the JS half and
      // was still writing the figure back in on every box change, which is why it survived
      // there after it had gone everywhere else.
      if (gpEl) gpEl.textContent = '';
      addBtn.disabled = u !== size;
      addBtn.textContent = u === size ? 'In den Warenkorb' : 'Box zuerst füllen';
    }

    root.addEventListener('click', function (e) {
      var s = e.target.closest('[data-size]');
      if (s) {
        size = parseInt(s.getAttribute('data-size'), 10);
        if (used() > size) picked = {};
        render();
        return;
      }
      var m = e.target.closest('[data-mix]');
      if (m) {
        var h = m.getAttribute('data-mix');
        var d = parseInt(m.getAttribute('data-delta'), 10);
        var cur = picked[h] || 0;
        var next = Math.max(0, cur + d);
        if (used() - cur + next > size) return;
        if (next) picked[h] = next; else delete picked[h];
        render();
      }
    });

    addBtn.addEventListener('click', function () {
      var entry = sizeEntry(size);
      if (!entry || !entry.variant_id || used() !== size) return;
      var note = Object.keys(picked).map(function (k) {
        var p = strains.filter(function (x) { return x.handle === k; })[0];
        return p.title + ' ' + picked[k] + ' g';
      }).join(', ');
      addBtn.disabled = true;
      cartFetch('/cart/add.js', {
        items: [{ id: entry.variant_id, quantity: 1, properties: { 'Deine Mischung': note } }]
      })
        .then(function () { return refreshCart(); })
        .then(function () { picked = {}; render(); openCart(); })
        .catch(function () { addBtn.disabled = false; });
    });

    render();
  }

  /* ---------- article table of contents ----------
     Built from the rendered body so the merchant writes normal H2s in the admin and the
     contents list, the anchors and the deep links stay in sync by construction. */
  function initToc() {
    var body = $('#hg-article-body');
    var toc = $('#hg-article-toc');
    if (!body || !toc) return;
    var heads = $$('h2', body);
    if (heads.length < 2) { toc.hidden = true; return; }
    toc.innerHTML = '<b>Inhalt</b><ol>' + heads.map(function (h, i) {
      if (!h.id) h.id = 'abschnitt-' + (i + 1);
      return '<li><a href="#' + h.id + '">' + h.textContent + '</a></li>';
    }).join('') + '</ol>';
  }

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    if (HG.closeNav) HG.closeNav();
    if (HG.closeSearch) HG.closeSearch();
    closeCart();
  });

  function boot() {
    initAgeGate();
    initCookie();
    initNav();
    initSearch();
    initCart();
    initCartTriggers();
    initAddForms();
    initQty();
    initGallery();
    initVariants();
    initPriceLadder();
    initFlavourBand();
    initMix();
    initToc();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // The theme editor re-renders sections without a page load.
  document.addEventListener('shopify:section:load', boot);
})();
